// ===== World 4: Toothbrush/Bath – FLOATING, SINKING AND BUBBLES =====
// Principle: light things float (bath pearl, sponge, boat), heavy things sink (marble, soap); air bubbles lift, and water brakes and carries things.
// Ball → goal: a little bath pearl (in some levels a heavy glass marble) → the happy tooth (bell) or the toothbrush mug (bowl).
import { defineWorld, defineObj } from '../../core/registry';
import { outline, face, bubble, selBox, clamp } from '../../core/draw-helpers';
import type { Blocker, Ctx2D, Gfx, GameBody, Level, ObjData, ObjDef, PartDef, Placement, Rect, Reskin, CorePartType, WorldDef, WorldMeta, SoundFn } from '../../core/types';
import { scene } from './scene';
import { levels } from './levels';
import { strings } from './strings';
import { K, DEG } from './shared';
import type { WaterProps } from './shared';

// ---------- constants ----------
const INK = '#3B2A5C';
/** Outline colour (slightly lighter than the shared default) */
const EDGE = 'rgba(59,42,92,.85)';
/** Bubble column of the bath whale: length and width */
const COL_L = 300, COL_W = 76;

/** Bodies in this world: float factor, moored (ignores the current) and how hard it rights itself in water */
interface BathBody extends GameBody { toothbrushF?: number; toothbrushMoor?: boolean; toothbrushRight?: number }
type WaterData = ObjData & WaterProps;
interface Splash { x: number; t: number }
interface WaterState { y: number; rising: boolean; splash: Splash[]; inside: Set<GameBody> }
interface BoatData extends ObjData { float?: number }
interface BoatState { b: BathBody; moored: boolean; cy: number }
interface Bubble { s: number; p: number; r: number; ph: number }
type GoalPos = { x: number; y: number; w: number };

// ---------- physics helpers ----------
const wrap = (a: number) => { while (a > Math.PI) a -= 2 * Math.PI; while (a < -Math.PI) a += 2 * Math.PI; return a; };

// Float factor: >1 floats (the bigger, the higher), <1 sinks
function floatOf(b: BathBody) {
  if (b.toothbrushF != null) return b.toothbrushF;
  if (b.kind === 'hero' || b.kind === 'helper') return b.skin === 'toothbrush_heavy_ball' ? 0.3 : 2.2;
  if (b.kind === 'stone') return 0.35;
  return 0.8;
}

// Narrow water holes "pull" sponge/soap to their middle (like the planks' magnet), so small fingers hit
let curLevel: Level | null = null;
function snapX(pl: Placement) {
  for (const obj of (curLevel && curLevel.objs) || []) {
    const o = obj as WaterData;
    if (o.t === 'toothbrush_water' && o.snap) {
      const mid = (o.x0 + o.x1) / 2;
      if (Math.abs(pl.x - mid) < (o.x1 - o.x0) / 2 + 55 && pl.y < o.y + 20) return mid;
    }
  }
  return pl.x;
}

// Bubble column frame: nozzle (nx, ny), direction d and its normal p
function colFrame(x: number, y: number, a: number) { const d = { x: Math.sin(a), y: -Math.cos(a) }; return { nx: x + d.x * 20, ny: y + d.y * 20, d, p: { x: -d.y, y: d.x } }; }

// Water surface
function waveY(st: WaterState, x: number, t: number) { return st.y + Math.sin(x * 0.035 + t * 0.06) * 2.2 + Math.sin(x * 0.013 - t * 0.04) * 1.5; }

// ---------- drawing helpers ----------
// The bath pearl (or heavy glass marble).
// Local, not the shared drawBall(): the rim is stroked after the shadow, the marble has a stripe, and the gloss is bigger (0.3 x 0.18) with a sparkle dot.
function drawBall(ctx: Ctx2D, b: GameBody, kind: string, t: number, g: Gfx) {
  const { x, y } = b.position, r = b.circleRadius!, heavy = b.skin === 'toothbrush_heavy_ball';
  ctx.save(); ctx.translate(x, y);
  g.shadowOn(ctx, 8, 5);
  const gr = ctx.createRadialGradient(-r * .35, -r * .4, r * .1, 0, 0, r * 1.05);
  if (heavy) { gr.addColorStop(0, '#E4E9FF'); gr.addColorStop(0.45, '#7D8CC9'); gr.addColorStop(1, '#3E4A8C'); }
  else if (kind === 'helper') { gr.addColorStop(0, '#E8FFF6'); gr.addColorStop(0.5, '#7FE0CF'); gr.addColorStop(1, '#3FB5A3'); }
  else { gr.addColorStop(0, '#FFE8F4'); gr.addColorStop(0.5, '#FF9FD0'); gr.addColorStop(1, '#E0569E'); }
  ctx.beginPath(); ctx.arc(0, 0, r, 0, 7); ctx.fillStyle = gr; ctx.fill(); g.shadowOff(ctx);
  ctx.lineWidth = 2; ctx.strokeStyle = heavy ? 'rgba(40,40,90,.7)' : 'rgba(170,40,110,.55)'; ctx.stroke();
  ctx.save(); ctx.rotate(b.roll || 0);
  if (heavy) { // the glass marble's twisted stripe
    ctx.strokeStyle = 'rgba(255,210,90,.85)'; ctx.lineWidth = 3;
    ctx.beginPath(); ctx.moveTo(-r * .9, r * .35); ctx.bezierCurveTo(-r * .3, r * .9, r * .3, -r * .1, r * .9, r * .45); ctx.stroke();
  }
  face(ctx, r, b.velocity, b.sad);
  ctx.restore();
  // gloss + the pearl's small sparkle
  ctx.beginPath(); ctx.ellipse(-r * .38, -r * .45, r * .3, r * .18, -0.6, 0, 7); ctx.fillStyle = 'rgba(255,255,255,.75)'; ctx.fill();
  if (!heavy) { ctx.beginPath(); ctx.arc(r * .5, -r * .55, r * .09, 0, 7); ctx.fill(); }
  ctx.restore();
}

// Toothbrush (plank) in a frame along x, length w, thickness h
function brush(ctx: Ctx2D, w: number, h: number, c1: string, c2: string, sel: boolean, g: Gfx) {
  g.block(ctx, 0, 0, w, h, 0, c1, c2, h / 2, { outline: sel ? '#FFC93C' : null });
  // white stripe on the handle
  ctx.fillStyle = 'rgba(255,255,255,.55)'; g.rr(ctx, -w / 2 + 16, -2, w * 0.45, 4, 2); ctx.fill();
  // the head with bristles (hangs below the handle, so the top is smooth to roll on)
  const hx = w / 2 - 30;
  ctx.fillStyle = '#FFFFFF'; g.rr(ctx, hx - 20, h / 2 - 2, 44, 8, 3); ctx.fill(); outline(ctx, 2, EDGE);
  for (let i = 0; i < 6; i++) {
    ctx.fillStyle = i % 2 ? '#7FD3FF' : '#FFFFFF';
    g.rr(ctx, hx - 18 + i * 7, h / 2 + 5, 5, 11, 2); ctx.fill();
    ctx.lineWidth = 1.2; ctx.strokeStyle = 'rgba(59,42,92,.6)'; ctx.stroke();
  }
}
function drawSponge(ctx: Ctx2D, w: number, h: number, g: Gfx, sel?: boolean) {
  g.block(ctx, 0, 0, w, h, 0, '#FFE77A', '#F2C230', 9, { outline: sel ? '#FFC93C' : null });
  ctx.fillStyle = '#5ED28A'; g.rr(ctx, -w / 2, -h / 2, w, h * 0.32, 8); ctx.fill(); outline(ctx, 2.5, EDGE);
  ctx.fillStyle = 'rgba(200,140,20,.45)';
  [[-30, 6, 4], [-12, 10, 3], [8, 5, 4.5], [26, 10, 3], [38, 3, 2.5], [-40, 11, 2.5], [-2, 13, 2]].forEach(([x, y, r]) => {
    if (Math.abs(x) < w / 2 - 6) { ctx.beginPath(); ctx.arc(x * w / 100, y * h / 36, r, 0, 7); ctx.fill(); }
  });
}
function drawSoap(ctx: Ctx2D, w: number, h: number, g: Gfx, sel?: boolean) {
  g.block(ctx, 0, 0, w, h, 0, '#FFC2DC', '#F07FAE', 14, { outline: sel ? '#FFC93C' : null });
  ctx.fillStyle = 'rgba(255,255,255,.55)'; g.rr(ctx, -w / 2 + 12, -4, w - 24, 10, 5); ctx.fill();
  ctx.strokeStyle = 'rgba(210,70,130,.5)'; ctx.lineWidth = 2; g.rr(ctx, -w / 2 + 12, -4, w - 24, 10, 5); ctx.stroke();
  ctx.fillStyle = 'rgba(255,255,255,.9)'; ctx.strokeStyle = 'rgba(59,42,92,.4)'; ctx.lineWidth = 1.5;
  [[-w / 2 + 6, -h / 2 - 6, 5], [-w / 2 + 16, -h / 2 - 9, 3.5], [w / 2 - 10, -h / 2 - 5, 4]].forEach(([x, y, r]) => { ctx.beginPath(); ctx.arc(x, y, r, 0, 7); ctx.fill(); ctx.stroke(); });
}
// The bath whale (the bubble machine). t is unused (the whale does not animate).
function drawWhale(ctx: Ctx2D, g: Gfx, t: number, sel?: boolean) {
  g.shadowOn(ctx, 8, 4);
  ctx.beginPath(); ctx.moveTo(30, 4); ctx.quadraticCurveTo(52, -2, 50, -20); ctx.quadraticCurveTo(44, -6, 34, -6); ctx.closePath();
  ctx.fillStyle = '#3E9BE0'; ctx.fill(); g.shadowOff(ctx); outline(ctx, 2.5, EDGE);
  ctx.beginPath(); ctx.ellipse(-2, 2, 38, 18, 0, 0, 7);
  ctx.fillStyle = g.lg(ctx, -16, 20, '#8FD8FF', '#3E9BE0'); g.shadowOn(ctx, 8, 4); ctx.fill(); g.shadowOff(ctx); outline(ctx, 3, EDGE);
  ctx.beginPath(); ctx.ellipse(-6, 10, 26, 7, 0, 0, Math.PI); ctx.fillStyle = 'rgba(255,255,255,.7)'; ctx.fill();
  ctx.beginPath(); ctx.arc(-22, -2, 4.5, 0, 7); ctx.fillStyle = '#fff'; ctx.fill(); ctx.beginPath(); ctx.arc(-21, -1.5, 2.6, 0, 7); ctx.fillStyle = INK; ctx.fill();
  ctx.strokeStyle = INK; ctx.lineWidth = 2; ctx.lineCap = 'round'; ctx.beginPath(); ctx.arc(-26, 5, 6, 0.1 * Math.PI, 0.7 * Math.PI); ctx.stroke();
  // blowhole
  g.rr(ctx, -6, -21, 12, 8, 3); ctx.fillStyle = '#2E78B8'; ctx.fill();
  // selection: an ellipse round the whale (not selBox)
  if (sel) { ctx.beginPath(); ctx.ellipse(0, 0, 50, 28, 0, 0, 7); ctx.strokeStyle = '#FFC93C'; ctx.lineWidth = 4; ctx.stroke(); }
}
function drawTooth(ctx: Ctx2D, x: number, y: number, t: number, hitT: number | undefined, g: Gfx) {
  const hit = hitT != null, k = hit ? Math.max(0, 1 - (t - hitT) / 60) : 0;
  const bob = Math.sin(t * 0.05) * 3, s = 1 + k * 0.15;
  ctx.save(); ctx.translate(x, y + bob); ctx.scale(s, s);
  // sparkles around it
  ctx.strokeStyle = hit ? '#FFC93C' : 'rgba(255,255,255,.95)'; ctx.lineWidth = 3; ctx.lineCap = 'round';
  for (let i = 0; i < 4; i++) {
    const a = i * Math.PI / 2 + t * 0.02, d = 42 + Math.sin(t * 0.1 + i) * 3;
    const px = Math.cos(a) * d, py = Math.sin(a) * d;
    ctx.beginPath(); ctx.moveTo(px - 5, py); ctx.lineTo(px + 5, py); ctx.moveTo(px, py - 5); ctx.lineTo(px, py + 5); ctx.stroke();
  }
  g.shadowOn(ctx, 10, 5);
  ctx.beginPath();
  ctx.moveTo(-26, -14); ctx.bezierCurveTo(-30, -34, -10, -36, 0, -26); ctx.bezierCurveTo(10, -36, 30, -34, 26, -14);
  ctx.bezierCurveTo(24, 4, 22, 18, 16, 30); ctx.quadraticCurveTo(10, 36, 6, 26); ctx.quadraticCurveTo(0, 14, -6, 26);
  ctx.quadraticCurveTo(-10, 36, -16, 30); ctx.bezierCurveTo(-22, 18, -24, 4, -26, -14); ctx.closePath();
  ctx.fillStyle = g.lg(ctx, -34, 34, '#FFFFFF', '#DCEBFA'); ctx.fill(); g.shadowOff(ctx); outline(ctx, 3, EDGE);
  ctx.beginPath(); ctx.ellipse(-14, -20, 5, 8, -0.5, 0, 7); ctx.fillStyle = 'rgba(255,255,255,.95)'; ctx.fill();
  for (const sx of [-1, 1]) { ctx.beginPath(); ctx.arc(sx * 9, -6, 3.2, 0, 7); ctx.fillStyle = INK; ctx.fill(); }
  ctx.strokeStyle = INK; ctx.lineWidth = 2.4; ctx.beginPath(); ctx.arc(0, 0, 8, 0.15 * Math.PI, 0.85 * Math.PI); ctx.stroke();
  ctx.fillStyle = 'rgba(255,120,150,.5)'; [-1, 1].forEach(sx => { ctx.beginPath(); ctx.ellipse(sx * 17, 2, 4, 2.5, 0, 0, 7); ctx.fill(); });
  ctx.restore();
}
// Toothpaste tube (reskinned pad): squeeze = push
function toothpaste(ctx: Ctx2D, r: Rect & { a: number }, sel: boolean, g: Gfx) {
  const w = r.w, h = r.h;
  ctx.save(); ctx.translate(r.x, r.y); ctx.rotate(r.a);
  g.shadowOn(ctx, 8, 4);
  ctx.beginPath(); ctx.moveTo(-w / 2, -h / 2 - 2); ctx.lineTo(w / 2 - 16, -h / 2 - 4); ctx.lineTo(w / 2 - 16, h / 2 + 8); ctx.lineTo(-w / 2, h / 2 + 4); ctx.closePath();
  ctx.fillStyle = g.lg(ctx, -h / 2, h / 2 + 8, '#FFFFFF', '#CFE6F7'); ctx.fill(); g.shadowOff(ctx); outline(ctx, 3, EDGE);
  ctx.fillStyle = '#FF5A5F'; ctx.fillRect(-w / 2 + 16, -h / 2 + 1, w - 40, 5);
  ctx.fillStyle = '#7FD3FF'; ctx.fillRect(-w / 2 + 16, h / 2 - 2, w - 40, 5);
  ctx.fillStyle = '#9FB3C8'; g.rr(ctx, -w / 2 - 6, -h / 2 - 4, 10, h + 10, 3); ctx.fill(); outline(ctx, 2, EDGE);
  g.rr(ctx, w / 2 - 17, -h / 2 + 1, 18, h + 2, 5); ctx.fillStyle = '#37C16B'; ctx.fill(); outline(ctx, 2.5, EDGE);
  if (sel) selBox(ctx, -w / 2 - 10, -h / 2 - 10, w + 16, h + 26, 10);
  ctx.restore();
}

// ---------- skins (statics, ball, goal, background) ----------
const skins: WorldDef['skins'] = {
  // bathtub / washbasin: white enamel
  toothbrush_tub(ctx: Ctx2D, b: GameBody, g: Gfx) { g.block(ctx, b.position.x, b.position.y, b.dw!, b.dh!, b.angle, '#FFFFFF', '#CDE6F5', Math.min(12, b.dw! / 2, b.dh! / 2)); },
  // light blue shelf with white stripes
  toothbrush_shelf(ctx: Ctx2D, b: GameBody, g: Gfx) {
    g.block(ctx, b.position.x, b.position.y, b.dw!, b.dh!, b.angle, '#BDEBFF', '#6FC3EA', Math.min(9, b.dh! / 2));
    ctx.save(); ctx.translate(b.position.x, b.position.y); ctx.rotate(b.angle);
    ctx.strokeStyle = 'rgba(255,255,255,.8)'; ctx.lineWidth = 2.5; ctx.lineCap = 'round';
    for (let x = -b.dw! / 2 + 30; x < b.dw! / 2 - 20; x += 90) { ctx.beginPath(); ctx.moveTo(x, -b.dh! / 2 + 4); ctx.lineTo(x + 10, b.dh! / 2 - 4); ctx.stroke(); }
    ctx.restore();
  },
  // mint tiles: joints across the long side
  toothbrush_tile(ctx: Ctx2D, b: GameBody, g: Gfx) {
    g.block(ctx, b.position.x, b.position.y, b.dw!, b.dh!, b.angle, '#A6EBD9', '#4FBFA5', Math.min(10, b.dw! / 2, b.dh! / 2));
    ctx.save(); ctx.translate(b.position.x, b.position.y); ctx.rotate(b.angle);
    ctx.strokeStyle = 'rgba(255,255,255,.65)'; ctx.lineWidth = 2;
    if (b.dh! > b.dw!) for (let y = -b.dh! / 2 + 34; y < b.dh! / 2 - 8; y += 34) { ctx.beginPath(); ctx.moveTo(-b.dw! / 2 + 3, y); ctx.lineTo(b.dw! / 2 - 3, y); ctx.stroke(); }
    else for (let x = -b.dw! / 2 + 34; x < b.dw! / 2 - 8; x += 34) { ctx.beginPath(); ctx.moveTo(x, -b.dh! / 2 + 3); ctx.lineTo(x, b.dh! / 2 - 3); ctx.stroke(); }
    ctx.restore();
  },
};

const ball: WorldDef['ball'] = drawBall;

const goal: WorldDef['goal'] = {
  // The toothbrush mug (rim ~58 px above the bottom)
  bowl(ctx: Ctx2D, gl: GoalPos, full: number, t: number, g: Gfx) {
    const { x, y, w } = gl;
    ctx.save();
    // toothbrushes in the mug (behind)
    ([[-w * .22, '#FF5A5F', -0.25], [w * .2, '#37C16B', 0.22]] as [number, string, number][]).forEach(([dx, c, a]) => {
      ctx.save(); ctx.translate(x + dx, y - 58); ctx.rotate(a);
      g.rr(ctx, -5, -62, 10, 70, 5); ctx.fillStyle = c; ctx.fill(); outline(ctx, 2, EDGE);
      g.rr(ctx, -7, -76, 14, 20, 4); ctx.fillStyle = '#fff'; ctx.fill(); outline(ctx, 2, EDGE);
      ctx.restore();
    });
    g.shadowOn(ctx, 14, 7);
    ctx.beginPath(); ctx.moveTo(x - w / 2 - 6, y - 60); ctx.lineTo(x - w / 2 + 6, y + 6); ctx.quadraticCurveTo(x, y + 14, x + w / 2 - 6, y + 6); ctx.lineTo(x + w / 2 + 6, y - 60); ctx.closePath();
    ctx.fillStyle = g.lg(ctx, y - 60, y + 10, '#FFE58A', '#F5B62A'); ctx.fill(); g.shadowOff(ctx); outline(ctx, 3, EDGE);
    // handle
    ctx.beginPath(); ctx.arc(x + w / 2 + 6, y - 28, 18, -Math.PI / 2, Math.PI / 2); ctx.lineWidth = 9; ctx.strokeStyle = '#F5B62A'; ctx.stroke();
    ctx.lineWidth = 2.5; ctx.strokeStyle = 'rgba(59,42,92,.7)'; ctx.beginPath(); ctx.arc(x + w / 2 + 6, y - 28, 22.5, -Math.PI / 2, Math.PI / 2); ctx.stroke();
    // dots
    ctx.fillStyle = 'rgba(255,255,255,.7)';
    for (let i = -2; i <= 2; i++) { ctx.beginPath(); ctx.arc(x + i * w / 6, y - 26 + (i % 2) * 8, 5, 0, 7); ctx.fill(); }
    // opening
    ctx.beginPath(); ctx.ellipse(x, y - 60, w / 2 + 6, 9, 0, 0, 7); ctx.fillStyle = '#C98A14'; ctx.fill();
    if (full) { ctx.beginPath(); ctx.ellipse(x, y - 58, w / 2 - 4, 6, 0, 0, 7); ctx.fillStyle = '#FF9FD0'; ctx.fill(); }
    ctx.restore();
  },
  front(ctx: Ctx2D, gl: GoalPos) {
    const { x, y, w } = gl;
    ctx.save(); ctx.beginPath(); ctx.ellipse(x, y - 60, w / 2 + 6, 9, 0, 0, Math.PI); ctx.lineWidth = 5; ctx.strokeStyle = '#FFE58A'; ctx.stroke(); ctx.restore();
  },
  // The happy tooth: the ball just has to touch it
  bell(ctx: Ctx2D, gl: { x: number; y: number; hitT?: number }, t: number, g: Gfx) { drawTooth(ctx, gl.x, gl.y, t, gl.hitT, g); },
};

const bgExtra: WorldDef['bgExtra'] = (ctx, W, H) => {
  // light tiles along the floor
  ctx.fillStyle = 'rgba(160,220,235,.18)'; ctx.fillRect(0, H - 34, W, 34);
  ctx.strokeStyle = 'rgba(255,255,255,.5)'; ctx.lineWidth = 2;
  for (let x = 0; x < W; x += 60) { ctx.beginPath(); ctx.moveTo(x, H - 34); ctx.lineTo(x, H); ctx.stroke(); }
};

// ---------- parts ----------
// Sponge intro (also shown for the soap): the sponge floats on the water, the soap sinks
function spongeIntro(ctx: Ctx2D, t: number, g: Gfx) {
  const wy = 190;
  ctx.fillStyle = 'rgba(90,190,255,.35)'; ctx.fillRect(40, wy, 380, 90);
  ctx.strokeStyle = '#fff'; ctx.lineWidth = 3; ctx.beginPath(); ctx.moveTo(40, wy); ctx.lineTo(420, wy); ctx.stroke();
  const k = (t % 240) / 240;
  const sy = k < 0.3 ? 60 + k / 0.3 * 140 : wy - 8 + Math.sin((k - 0.3) * 30) * 6 * Math.exp(-(k - 0.3) * 8);
  ctx.save(); ctx.translate(150, sy); drawSponge(ctx, 100, 36, g); ctx.restore();
  const soapY = k < 0.3 ? 60 + k / 0.3 * 140 : Math.min(262, 200 + (k - 0.3) * 200);
  ctx.save(); ctx.translate(320, soapY); drawSoap(ctx, 70, 36, g); ctx.restore();
}

const parts: Record<string, PartDef> = {
  // Bath sponge: full of air – floats high. Can be a bridge or a raft.
  toothbrush_sponge: {
    rects: [{ x: 0, y: 0, w: 100, h: 36 }], rot: false,
    draw(ctx, pl, sel, g) { ctx.save(); ctx.translate(snapX(pl), pl.y); drawSponge(ctx, 100, 36, g, sel); ctx.restore(); },
    icon(ctx, g) { ctx.save(); ctx.scale(0.9, 0.9); drawSponge(ctx, 100, 36, g); ctx.restore(); },
    create(api, pl) {
      const b = api.add(api.R(snapX(pl), pl.y, 100, 36, { density: 0.0008, friction: 0.9, frictionStatic: 1, restitution: 0.05, frictionAir: 0.01, chamfer: { radius: 8 }, label: 'toothbrush_sponge' })) as BathBody;
      b.kind = 'toothbrush_sponge'; b.toothbrushF = 7; b.toothbrushMoor = true; api.dynamic(b);
      api.onHit(b, o => { if (api.isBall(o)) { b.toothbrushMoor = false; if (o.speed > 2) api.sfx('toothbrush_blub', b.position.x, b.position.y - 20); } });
      return { layer: 'mid', draw(ctx, t, g) { ctx.save(); ctx.translate(b.position.x, b.position.y); ctx.rotate(b.angle); drawSponge(ctx, 100, 36, g); ctx.restore(); } };
    },
    intro(ctx, t, g) { spongeIntro(ctx, t, g); },
  },
  // Soap: heavy and slippery – sinks.
  toothbrush_soap: {
    rects: [{ x: 0, y: 0, w: 70, h: 36 }], rot: false,
    draw(ctx, pl, sel, g) { ctx.save(); ctx.translate(snapX(pl), pl.y); drawSoap(ctx, 70, 36, g, sel); ctx.restore(); },
    icon(ctx, g) { drawSoap(ctx, 70, 36, g); },
    create(api, pl) {
      const b = api.add(api.R(snapX(pl), pl.y, 70, 36, { density: 0.005, friction: 0.02, frictionStatic: 0.02, restitution: 0.05, frictionAir: 0.002, chamfer: { radius: 12 }, label: 'toothbrush_soap' })) as BathBody;
      b.kind = 'toothbrush_soap'; b.toothbrushF = 0.5; api.dynamic(b);
      let last = -99;
      api.onHit(b, () => { if (b.speed > 3 && api.S.t - last > 20) { last = api.S.t; api.sfx('toothbrush_clonk', b.position.x, b.position.y, '#FFC2DC', 5); } });
      return { layer: 'mid', draw(ctx, t, g) { ctx.save(); ctx.translate(b.position.x, b.position.y); ctx.rotate(b.angle); drawSoap(ctx, 70, 36, g); ctx.restore(); } };
    },
    intro(ctx, t, g) { spongeIntro(ctx, t, g); },
  },
  // Bath whale: blows a column of air bubbles upwards. Air pushes up – light things are lifted most.
  toothbrush_bubbles: {
    rects: [{ x: 0, y: 4, w: 76, h: 30 }], rot: 15, minA: -30, maxA: 30, defaultA: 0,
    draw(ctx, pl, sel, g) {
      const a = (pl.a || 0) * DEG, F = colFrame(pl.x, pl.y, a);
      // show the column's reach faintly
      ctx.save(); ctx.translate(F.nx, F.ny); ctx.rotate(a);
      ctx.fillStyle = 'rgba(255,255,255,.22)'; g.rr(ctx, -COL_W / 2, -COL_L, COL_W, COL_L, 30); ctx.fill();
      ctx.setLineDash([6, 8]); ctx.strokeStyle = 'rgba(62,155,224,.45)'; ctx.lineWidth = 2.5; ctx.stroke(); ctx.setLineDash([]);
      [[0, -40, 6], [-10, -110, 9], [8, -190, 7], [-4, -262, 10]].forEach(([x, y, r]) => bubble(ctx, x, y, r));
      ctx.restore();
      ctx.save(); ctx.translate(pl.x, pl.y); ctx.rotate(a); drawWhale(ctx, g, 0, sel); ctx.restore();
    },
    icon(ctx, g) { ctx.save(); ctx.translate(0, 18); ctx.scale(0.85, 0.85); drawWhale(ctx, g, 0); ctx.restore(); bubble(ctx, 0, -24, 7); bubble(ctx, 8, -44, 5); },
    create(api, pl) {
      const a = (pl.a || 0) * DEG, F = colFrame(pl.x, pl.y, a);
      const base = api.add(api.R(pl.x - Math.sin(a) * 4, pl.y + Math.cos(a) * 4, 76, 30, { isStatic: true, angle: a, friction: 0.1, restitution: 0.1, chamfer: { radius: 12 }, label: 'toothbrush_bubbles' }));
      base.kind = 'toothbrush_bubbles';
      const bub: Bubble[] = [];
      let puff = 0;
      return {
        layer: 'mid',
        step(api) {
          const S = api.S, Body = api.Body, gy = api.engine.gravity.y * 0.001;
          if (S.t % 5 === 0) bub.push({ s: 0, p: (((S.t * 37) % 17) - 8) * 1.6, r: 4 + (S.t * 13) % 6, ph: S.t * 0.7 });
          for (let i = bub.length - 1; i >= 0; i--) { const q = bub[i]; q.s += 3.2 + q.r * 0.15; q.r += 0.02; if (q.s > COL_L) bub.splice(i, 1); }
          for (const b of S.dyn) {
            if (b.isStatic || b.kind === 'seesaw') continue;
            const dx = b.position.x - F.nx, dy = b.position.y - F.ny;
            const along = dx * F.d.x + dy * F.d.y, perp = dx * F.p.x + dy * F.p.y;
            if (along < -10 || along > COL_L || Math.abs(perp) > COL_W / 2 + 6) continue;
            const fade = along > COL_L - 70 ? (COL_L - along) / 70 : 1;
            const f = 3.6e-6 * b.area * fade * gy / 0.001;
            Body.applyForce(b, b.position, { x: F.d.x * f, y: F.d.y * f });
            // keep it inside the column (like a table-tennis ball in a hair dryer)
            const vp = b.velocity.x * F.p.x + b.velocity.y * F.p.y, dv = -vp * 0.06 - perp * 0.004;
            Body.setVelocity(b, { x: b.velocity.x + F.p.x * dv, y: b.velocity.y + F.p.y * dv });
            if (api.isBall(b) && S.t - puff > 40) { puff = S.t; api.sfx('toothbrush_blub', b.position.x, b.position.y); }
          }
        },
        draw(ctx, t, g) {
          ctx.save(); ctx.translate(F.nx, F.ny); ctx.rotate(a);
          for (const q of bub) bubble(ctx, q.p + Math.sin(q.ph + q.s * 0.05) * 5, -q.s, q.r);
          ctx.restore();
          ctx.save(); ctx.translate(pl.x, pl.y); ctx.rotate(a); drawWhale(ctx, g, t); ctx.restore();
        },
      };
    },
    intro(ctx, t, g, h) {
      ctx.fillStyle = 'rgba(90,190,255,.35)'; ctx.fillRect(40, 130, 380, 150);
      ctx.strokeStyle = '#fff'; ctx.lineWidth = 3; ctx.beginPath(); ctx.moveTo(40, 130); ctx.lineTo(420, 130); ctx.stroke();
      ctx.save(); ctx.translate(230, 255); drawWhale(ctx, g, t); ctx.restore();
      for (let i = 0; i < 8; i++) { const s = (t * 2.2 + i * 30) % 240; bubble(ctx, 230 + Math.sin(i * 2 + s * 0.05) * 6, 232 - s, 4 + (i % 3) * 2); }
      const k = (t % 240) / 240, by = 118 - h.ease(Math.min(1, k * 1.6)) * 80;
      g.ball(ctx, h.fakeBall(230, by, 0, -1, 16), 'hero', t);
    },
  },
};

// ---------- objects ----------
declare module '../../core/types' {
  // level data: the water spans x0..x1 (no x), see WaterProps
  interface ObjDataTypes { toothbrush_water: WaterProps }
}
// Level setup: remember the level, so snapX can find its narrow water holes
const setup: WorldDef['setup'] = (api, level) => { curLevel = level; };

const objs: Record<string, ObjDef> = {
  // Water: { x0, x1, y (surface), y1 (bottom), flow (current, px/frame), rise: { to, v, on } } – see WaterProps
  toothbrush_water: defineObj<WaterState>({
    layer: 'front',
    create(api, obj) {
      const o = obj as WaterData, rise = o.rise;
      const st: WaterState = { y: o.y, rising: !!(rise && !rise.on), splash: [], inside: new Set() };
      if (rise && rise.on) api.onAct(rise.on, () => { st.rising = true; api.sfx('toothbrush_splash', rise.tx || o.x0 + 30, st.y); });
      return st;
    },
    start(api, st) { const o = st.o as WaterData; if (st.rising && o.rise) api.sfx('toothbrush_splash', o.rise.tx || o.x0 + 30, st.y); },
    step(api, st) {
      const o = st.o as WaterData, S = api.S, Body = api.Body, gy = api.engine.gravity.y * 0.001;
      // rising is only ever set when the water has a rise
      if (st.rising) { const rise = o.rise!; st.y -= rise.v || 0.8; if (st.y <= rise.to) { st.y = rise.to; st.rising = false; } }
      const drag = o.drag || 0.06, flow = o.flow || 0;
      for (const body of S.dyn) {
        const b = body as BathBody;
        if (b.isStatic || b.kind === 'seesaw') continue;
        const x = b.position.x;
        if (x < o.x0 || x > o.x1 || b.bounds.min.y > o.y1 + 4) { st.inside.delete(b); continue; }
        const top = b.bounds.min.y, bot = b.bounds.max.y, hh = Math.max(1, bot - top);
        const f = clamp((bot - st.y) / hh, 0, 1);
        if (f <= 0) { st.inside.delete(b); continue; }
        if (!st.inside.has(b)) {
          st.inside.add(b);
          if (b.velocity.y > 2.5) { api.sfx('toothbrush_splash', x, st.y, 'rgba(160,220,255,.9)', 10); st.splash.push({ x, t: S.t }); }
        }
        Body.applyForce(b, b.position, { x: 0, y: -b.mass * gy * floatOf(b) * f });
        const d = 1 - drag * f;
        const fl = b.toothbrushMoor ? 0 : flow;
        Body.setVelocity(b, { x: fl + (b.velocity.x - fl) * d, y: b.velocity.y * d });
        let av = b.angularVelocity * (1 - 0.08 * f);
        if (!b.circleRadius) av -= wrap(b.angle) * (b.toothbrushRight || 0.004) * Math.max(f, b.toothbrushRight ? 0.5 : 0);
        Body.setAngularVelocity(b, av);
      }
      if (st.splash.length && S.t - st.splash[0].t > 40) st.splash.shift();
    },
    busy(api, st) { return st.rising; },
    // noplace: y – no parts may be placed here (from y down to the water), e.g. under a shelf
    blockers(obj) {
      const o = obj as WaterData;
      const out: Blocker[] = [];
      if (o.noplace != null) for (let x = o.x0 - 10; x <= o.x1 + 10; x += 40) for (let y = o.noplace; y <= o.y + 30; y += 40) out.push({ x, y, r: 26 });
      return out;
    },
    draw(ctx, st, t, g, S) {
      const o = st.o as WaterData, x0 = o.x0, x1 = o.x1, y1 = o.y1;
      ctx.save();
      ctx.beginPath(); ctx.moveTo(x0, y1);
      for (let x = x0; x <= x1; x += 12) ctx.lineTo(x, waveY(st, x, t));
      ctx.lineTo(x1, waveY(st, x1, t)); ctx.lineTo(x1, y1); ctx.closePath();
      ctx.fillStyle = g.lg(ctx, st.y, y1, 'rgba(110,205,255,.42)', 'rgba(40,140,230,.5)'); ctx.fill();
      // surface
      ctx.beginPath(); for (let x = x0; x <= x1; x += 12) ctx.lineTo(x, waveY(st, x, t)); ctx.lineTo(x1, waveY(st, x1, t));
      ctx.lineWidth = 4; ctx.strokeStyle = 'rgba(255,255,255,.9)'; ctx.stroke();
      // small glints in the water
      ctx.strokeStyle = 'rgba(255,255,255,.45)'; ctx.lineWidth = 3; ctx.lineCap = 'round';
      for (let x = x0 + 30; x < x1 - 30; x += 90) { const yy = st.y + 18 + ((x * 7) % 3) * 12; if (yy < y1 - 8) { ctx.beginPath(); ctx.moveTo(x + Math.sin(t * 0.03 + x) * 6, yy); ctx.lineTo(x + 22 + Math.sin(t * 0.03 + x) * 6, yy); ctx.stroke(); } }
      // current arrows (show the water's direction)
      if (o.flow) {
        ctx.strokeStyle = 'rgba(255,255,255,.55)'; ctx.lineWidth = 3;
        const dir = Math.sign(o.flow), span = x1 - x0 - 80, sy = Math.min(y1 - 12, st.y + 34);
        for (let i = 0; i < 3; i++) {
          const px = x0 + 40 + ((t * Math.abs(o.flow) * 0.5 + i * span / 3) % span), ax = dir > 0 ? px : x1 - (px - x0);
          ctx.beginPath(); ctx.moveTo(ax - dir * 14, sy - 7); ctx.lineTo(ax, sy); ctx.lineTo(ax - dir * 14, sy + 7); ctx.stroke();
        }
      }
      // splashes
      for (const s of st.splash) {
        const k = (S.t - s.t) / 40;
        ctx.strokeStyle = `rgba(255,255,255,${0.9 * (1 - k)})`; ctx.lineWidth = 3;
        ctx.beginPath(); ctx.ellipse(s.x, st.y, 10 + k * 40, 3 + k * 6, 0, 0, 7); ctx.stroke();
      }
      // tap
      if (o.rise) {
        const tx = o.rise.tx || x0 + 40, ty = o.rise.ty || 60, on = st.rising;
        if (on) { ctx.fillStyle = 'rgba(120,210,255,.7)'; ctx.fillRect(tx - 6, ty + 16, 12 + Math.sin(t * 0.5), st.y - ty - 16); ctx.fillStyle = 'rgba(255,255,255,.6)'; ctx.fillRect(tx - 2, ty + 16, 3, st.y - ty - 16); }
        g.shadowOn(ctx, 8, 4);
        g.rr(ctx, tx - 40, ty - 18, 52, 18, 8); ctx.fillStyle = g.lg(ctx, ty - 18, ty, '#F2F5FA', '#AEB8C8'); ctx.fill(); g.shadowOff(ctx); outline(ctx, 2.5, EDGE);
        g.rr(ctx, tx - 9, ty - 12, 18, 32, 7); ctx.fillStyle = g.lg(ctx, ty - 12, ty + 20, '#F2F5FA', '#AEB8C8'); ctx.fill(); outline(ctx, 2.5, EDGE);
        ctx.save(); ctx.translate(tx - 30, ty - 22); ctx.rotate(on ? t * 0.05 : 0);
        for (let i = 0; i < 4; i++) { ctx.rotate(Math.PI / 2); g.rr(ctx, -4, -16, 8, 16, 4); ctx.fillStyle = i % 2 ? '#FF5A5F' : '#FF8A8A'; ctx.fill(); }
        ctx.beginPath(); ctx.arc(0, 0, 5, 0, 7); ctx.fillStyle = '#fff'; ctx.fill(); outline(ctx, 2, EDGE);
        ctx.restore();
      }
      ctx.restore();
    },
    intro(ctx, t, g, h) {
      const wy = 160;
      ctx.fillStyle = 'rgba(90,190,255,.4)'; ctx.fillRect(40, wy, 380, 110);
      ctx.strokeStyle = '#fff'; ctx.lineWidth = 3; ctx.beginPath(); ctx.moveTo(40, wy); ctx.lineTo(420, wy); ctx.stroke();
      const k = (t % 240) / 240;
      // the pearl falls in, bobs up and floats
      let y1 = k < 0.2 ? 40 + k / 0.2 * 150 : k < 0.35 ? 190 - (k - 0.2) / 0.15 * 45 : wy - 2 + Math.sin(k * 40) * 2;
      g.ball(ctx, h.fakeBall(150 + Math.max(0, k - 0.35) * 150, y1, 0, 0, 16), 'hero', t);
      // the glass marble falls in and sinks
      const fb = h.fakeBall(330, k < 0.2 ? 40 + k / 0.2 * 150 : Math.min(250, 190 + (k - 0.2) * 150), 0, 0, 16); fb.skin = 'toothbrush_heavy_ball';
      g.ball(ctx, fb, 'hero', t);
    },
  }),
  // Toy boat that floats and can carry a heavy ball: { x, y }
  toothbrush_boat: defineObj<BoatState>({
    layer: 'mid',
    create(api, obj) {
      const o = obj as BoatData;
      const { Bodies, Body } = api;
      const mk = (x: number, y: number, w: number, h: number, a?: number) => { const b = Bodies.rectangle(x, y, w, h, { angle: a || 0, chamfer: { radius: 4 } }) as GameBody; b.dw = w; b.dh = h; return b; };
      const parts = [mk(o.x, o.y, 140, 14), mk(o.x - 72, o.y - 22, 12, 52, -18 * DEG), mk(o.x + 72, o.y - 22, 12, 52, 18 * DEG)];
      const b = Body.create({ parts, friction: 0.4, restitution: 0.05, frictionAir: 0.01 }) as BathBody;
      Body.setDensity(b, 0.0012);
      api.add(b); b.kind = 'toothbrush_boat'; b.toothbrushF = o.float || 7; b.toothbrushRight = 0.02; api.dynamic(b);
      const st: BoatState = { b, moored: true, cy: o.y - b.position.y };
      // the boat is moored until something lands in it
      api.onHit(b, x => { if (api.isBall(x) || (x.kind && x.kind.startsWith('toothbrush_'))) st.moored = false; });
      return st;
    },
    step(api, st) {
      const b = st.b;
      if (st.moored) api.Body.setVelocity(b, { x: (st.o.x - b.position.x) * 0.1, y: b.velocity.y });
      api.Body.setAngularVelocity(b, b.angularVelocity * 0.9);
    },
    blockers(o) { return [{ x: o.x - 50, y: o.y - 20, r: 40 }, { x: o.x + 50, y: o.y - 20, r: 40 }]; },
    draw(ctx, st, t, g) {
      const b = st.b;
      ctx.save(); ctx.translate(b.position.x, b.position.y); ctx.rotate(b.angle);
      ctx.translate(0, st.cy);   // the centre of mass sits a little above the bottom
      // mast + sail
      ctx.fillStyle = '#B8612A'; g.rr(ctx, -3, -110, 6, 104, 3); ctx.fill();
      ctx.beginPath(); ctx.moveTo(4, -106); ctx.lineTo(50, -30); ctx.lineTo(4, -30); ctx.closePath(); ctx.fillStyle = '#FFFFFF'; ctx.fill(); outline(ctx, 2.5, EDGE);
      ctx.fillStyle = '#FF5A5F'; ctx.beginPath(); ctx.moveTo(-3, -110); ctx.lineTo(-24, -102); ctx.lineTo(-3, -95); ctx.fill();
      // hull
      g.shadowOn(ctx, 8, 4);
      ctx.beginPath(); ctx.moveTo(-82, -46); ctx.lineTo(82, -46); ctx.lineTo(62, 8); ctx.quadraticCurveTo(0, 16, -62, 8); ctx.closePath();
      ctx.fillStyle = g.lg(ctx, -46, 10, '#FF8A8A', '#E0394A'); ctx.fill(); g.shadowOff(ctx); outline(ctx, 3, EDGE);
      ctx.fillStyle = 'rgba(255,255,255,.8)'; g.rr(ctx, -70, -40, 140, 7, 3); ctx.fill();
      ctx.fillStyle = '#FFE07A'; [-40, 0, 40].forEach(x => { ctx.beginPath(); ctx.arc(x, -16, 6, 0, 7); ctx.fill(); outline(ctx, 2, EDGE); });
      ctx.restore();
    },
  }),
};

// ---------- sounds ----------
const sounds: Record<string, SoundFn> = {
  toothbrush_blub: (tone) => { tone(320, 0.12, 'sine', 0.18, 720); tone(480, 0.1, 'sine', 0.12, 900, 0.07); },
  toothbrush_splash: (tone, noise) => { noise(0.35, 0.3, 900, 0.8); tone(200, 0.2, 'sine', 0.15, 90); },
  toothbrush_clonk: (tone, noise) => { noise(0.25, 0.25, 500, 1.2); tone(140, 0.25, 'sine', 0.2, 70); },
};

// ---------- reskins (the kit's planks as toothbrushes, the pad as a toothpaste tube) ----------
const reskin: Partial<Record<CorePartType, Reskin>> = {
  plank(ctx, pl, sel, g, rs) { const r = rs[0]; ctx.save(); ctx.translate(r.x, r.y); ctx.rotate(r.a); brush(ctx, r.w, r.h, '#FF7A7A', '#E0394A', sel, g); ctx.restore(); },
  long_plank(ctx, pl, sel, g, rs) { const r = rs[0]; ctx.save(); ctx.translate(r.x, r.y); ctx.rotate(r.a); brush(ctx, r.w, r.h, '#8FD8FF', '#3E9BE0', sel, g); ctx.restore(); },
  pad(ctx, pl, sel, g, rs) { toothpaste(ctx, rs[0], sel, g); },
};
const icons: WorldDef['icons'] = {
  plank(ctx, g) { ctx.save(); ctx.rotate(-0.35); brush(ctx, 100, 16, '#FF7A7A', '#E0394A', false, g); ctx.restore(); },
  long_plank(ctx, g) { ctx.save(); ctx.rotate(-0.35); brush(ctx, 110, 14, '#8FD8FF', '#3E9BE0', false, g); ctx.restore(); },
  pad(ctx, g) { toothpaste(ctx, { x: 0, y: -4, a: 0, w: 86, h: 16 }, false, g); },
};

// ---------- meta ----------
const meta: WorldMeta = {
  icon: '🪥', strings, music: 'toothbrush.mp3',
  credits: { title: 'Ocean Bubbles', artist: '9JackJack8', id: 550245, url: 'https://pixabay.com/music/ambient-ocean-bubbles-frutiger-aqua-game-550245/' },
  synth: { bpm: 68, root: 329.6, sc: 'major', prog: [0, 3, 5, 4], bar: 4, lead: 'soft', dens: 0.3 },
};

const world = defineWorld({
  k: K,
  meta,
  scene,
  wash: 'rgba(240,252,255,.6)',
  setup,
  bgExtra,
  skins,
  ball,
  goal,
  sounds,
  reskin,
  icons,
  parts,
  objs,
  levels,
});

export default world;
